import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { teamMembersAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Plus, 
  Users,
  Trash2,
  Mail,
  User,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BRAND_NAMES = {
  "thelocaljewel": "TheLocalJewel",
  "justlabs": "Justlabs"
};

const TeamMembers = () => {
  const { activeBrand, getActiveBrandName } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [newMember, setNewMember] = useState({
    email: '',
    password: '',
    name: '',
    brand: '',
  });

  useEffect(() => {
    if (activeBrand) {
      setNewMember(prev => ({ ...prev, brand: activeBrand }));
      fetchMembers();
    }
  }, [activeBrand]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await teamMembersAPI.getAll(activeBrand);
      setMembers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access restricted to founder only');
      } else {
        toast.error('Failed to fetch team members');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async () => {
    if (!newMember.email || !newMember.password || !newMember.name) {
      toast.error('All fields are required');
      return;
    }

    if (newMember.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await teamMembersAPI.create(newMember);
      toast.success('Team member added!');
      setShowAddSheet(false);
      setNewMember({
        email: '',
        password: '',
        name: '',
        brand: activeBrand,
      });
      fetchMembers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create team member');
      }
    }
  };

  const handleDeleteMember = async (member) => {
    try {
      await teamMembersAPI.delete(member.id);
      toast.success('Team member removed');
      setDeleteDialog(null);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="team-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-zinc-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="team-members-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Team Members
          </h1>
          <p className="text-zinc-500 mt-1">
            Manage team for <span className="font-medium text-zinc-700">{getActiveBrandName()}</span>
          </p>
        </div>
        
        <Button
          onClick={() => setShowAddSheet(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          data-testid="add-member-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-100 p-4 text-center">
          <p className="text-3xl font-medium text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            {members.length}
          </p>
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Team Members</p>
        </div>
        <div className="bg-white border border-zinc-100 p-4 text-center">
          <p className="text-3xl font-medium text-emerald-600" style={{ fontFamily: 'Playfair Display, serif' }}>
            {getActiveBrandName()}
          </p>
          <p className="text-xs text-zinc-400 uppercase tracking-widest">Active Workspace</p>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white border border-zinc-100">
        <div className="px-6 py-4 border-b border-zinc-100">
          <h2 className="text-sm font-medium text-zinc-900 uppercase tracking-widest">
            {getActiveBrandName()} Team
          </h2>
        </div>
        
        {members.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            {members.map((member) => (
              <div 
                key={member.id}
                className="flex items-center gap-4 p-4 hover:bg-zinc-50 transition-colors"
                data-testid={`member-${member.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-zinc-500" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{member.name}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <Mail className="w-3 h-3" />
                      {member.email}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <Building2 className="w-3 h-3" />
                      {BRAND_NAMES[member.brand] || member.brand}
                    </span>
                  </div>
                </div>
                
                <span className="text-xs text-zinc-400">
                  Added {formatDate(member.created_at)}
                </span>
                
                <button
                  onClick={() => setDeleteDialog(member)}
                  className="text-zinc-300 hover:text-red-500 transition-colors p-2"
                  data-testid={`delete-member-${member.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              No team members yet
            </h3>
            <p className="text-zinc-500 mb-6">
              Add your first team member for {getActiveBrandName()}
            </p>
            <Button
              onClick={() => setShowAddSheet(true)}
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
            >
              Add Member
            </Button>
          </div>
        )}
      </div>

      {/* Add Member Sheet */}
      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="add-member-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Add Team Member
            </SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">
              Create a new account for {getActiveBrandName()}
            </p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Enter full name"
                data-testid="add-member-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Enter email address"
                data-testid="add-member-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  className="w-full px-4 py-3 pr-12 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                  placeholder="Create a password"
                  data-testid="add-member-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Workspace
              </label>
              <Select
                value={newMember.brand}
                onValueChange={(value) => setNewMember({ ...newMember, brand: value })}
              >
                <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-member-brand-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="thelocaljewel">TheLocalJewel</SelectItem>
                  <SelectItem value="justlabs">Justlabs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-amber-50 border border-amber-100 p-4 rounded-md">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The team member will use these credentials to log in. 
                Make sure to share the email and password with them securely.
              </p>
            </div>

            <Button
              onClick={handleCreateMember}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="save-member-btn"
            >
              Create Account
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-member-dialog">
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{deleteDialog?.name}</strong> ({deleteDialog?.email}) from {BRAND_NAMES[deleteDialog?.brand]}?
              This will also delete all their tasks.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteMember(deleteDialog)}
              data-testid="confirm-delete-member-btn"
            >
              Remove Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMembers;
